import type {ReactElement} from 'react';
import {useState} from 'react';
import type {NotificationsListProps} from './notifications-list.types';
import styles from './notifications-list.module.scss';
import {NotificationsListItem} from '@Panels/notifications-panel/components/notifications-list-item/notifications-list-item';
import {NotificationsThumbnail} from '@Panels/notifications-panel/components/notifications-thumbnail/notifications-thumbnail';
import {
  type Notification,
  NotificationApprovalStatus,
  NotificationIconType,
  type NotificationsItemData,
  NotificationStatus,
} from '@Panels/notifications-panel/notifications-panel.types';
import {Text, TextSize} from '@Components/text';
import {NotificationsApprovalButtons} from '@Panels/notifications-panel/components/notifications-approval-buttons/notifications-approval-buttons';
import {getIconStyles, handleRedirectionLink, markClickedNotificationAsRead} from '@Libraries/notifications-panel-library';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {useAllNotifications} from '@Panels/notifications-panel/notifications-panel-hooks';

export function NotificationsList({...props}: NotificationsListProps): ReactElement | null {
  const [isListItemHovered, setIsListItemHovered] = useState<string | null>(null);
  const allNotifications = useAllNotifications();

  if (props.notifications.length === 0) {
    return null;
  }

  const shouldEnableListItemOnClick = (notification: Notification, itemData: NotificationsItemData): boolean => {
    if (!itemData.buttons) {
      return true;
    }

    return (
      itemData.buttons.length > 0 &&
      notification.status === NotificationStatus.READ &&
      (itemData.approvalStatus === NotificationApprovalStatus.APPROVED || itemData.approvalStatus === NotificationApprovalStatus.DECLINED)
    );
  };

  const onClickNotificationsListItem = (notification: Notification, notificationItemData: NotificationsItemData): void => {
    if (!shouldEnableListItemOnClick(notification, notificationItemData)) {
      return;
    }

    const isAggregated = notificationItemData.sharedBy.length > 1;
    const redirectionLink = isAggregated ? (notificationItemData.aggregationRedirectionLink ?? notificationItemData.redirectionLink) : notificationItemData.redirectionLink;
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.NOTIFICATION_CLICK, {[GA4EventParamName.VALUE]: notification.notificationCategory});

    if (notification.status === NotificationStatus.READ) {
      handleRedirectionLink(redirectionLink);
    } else {
      markClickedNotificationAsRead(notification, allNotifications, redirectionLink);
    }
    props.customClose();
  };
  const showAggregatedText = (notificationItemData: NotificationsItemData): boolean => {
    if (notificationItemData.allowAggregation) {
      if (notificationItemData.totalUsers && notificationItemData.totalUsers > 1) {
        return true;
      } else if (notificationItemData.sharedBy && notificationItemData.sharedBy.length > 1) {
        return true;
      }
    }
    return false;
  };

  const getNotificationListItem = (notification: Notification, index: number): ReactElement => {
    const notificationItemData = JSON.parse(notification.itemData) as NotificationsItemData;
    const iconStyles = getIconStyles(notificationItemData.notificationMiniIconType ?? NotificationIconType.NEUTRAL);

    return (
      <div
        key={`NotificationListStatus-${index}`}
        onMouseEnter={() => {
          setIsListItemHovered(notification.hashedId);
        }}
        onMouseLeave={() => {
          setIsListItemHovered(null);
        }}
      >
        <NotificationsListItem
          notificationsThumbnail={
            <NotificationsThumbnail
              miniIconName={notificationItemData.notificationMiniIcon ?? ''}
              miniIconClasses={iconStyles.className}
              hoveredMiniIconClasses={iconStyles.hoverClassName}
              iconName={notificationItemData.notificationIcon}
              imageUrl={notificationItemData.imageUrl}
              thumbnailClassName={notificationItemData.imageUrl ? '' : 'bg-primary-10'}
              isHovered={isListItemHovered === notification.hashedId}
            />
          }
          heading={showAggregatedText(notificationItemData) ? (notificationItemData.aggregationText ?? '') : (notificationItemData.notificationTitle ?? '')}
          buttons={notificationItemData.buttons ? <NotificationsApprovalButtons notification={notification} /> : null}
          showPulse={notification.status === NotificationStatus.NEW}
          timePassed={notification.createdOn as unknown as number}
          isRead={notification.status === NotificationStatus.READ}
          onClick={(): void => {
            onClickNotificationsListItem(notification, notificationItemData);
          }}
          userComment={notificationItemData.comment}
        />
      </div>
    );
  };
  return (
    <div className={props.className}>
      <div className={styles.panelWrapper}>
        <Text val={props.heading ?? ''} bold size={TextSize.XXSMALL}></Text>
        <div className={styles.listItemSpacing}>
          {props.notifications.map((notification, index) => {
            return getNotificationListItem(notification, index);
          })}
        </div>
      </div>
    </div>
  );
}
